/**
 * Keywords that trigger the GitHub tip banner in Quick Find.
 *
 * Sourced from the experiment spec:
 * https://hello.atlassian.net/wiki/spaces/CentAI/pages/6820699210/Driving+User+connections+of+GitHub+in+Rovo
 *
 * Matching is whole-word case-insensitive, so the order in this list is
 * documentation-only and does not affect which queries match.
 */
export const GITHUB_TIP_KEYWORDS = [
	// add words between git -> gihub into the list,
	// so it won't flash on and off as the user types
	'git',
	'gith',
	'githu',
	'github',
	'repo',
	'repository',
	'pr',
	'prs',
	'pull request',
	'pull requests',
	'merge request',
	'commit',
	'branch',
	'merge',
	'fork',
	'hotfix',
	// add words between deploy -> deployment into the list,
	// so it won't flash on and off as the user types
	'deploy',
	'deploym',
	'deployme',
	'deploymen',
	'deployment',
	'release',
	'code review',
	'pipeline',
	'ci',
	'cd',
	'revert',
] as const;

export type GithubTipKeyword = (typeof GITHUB_TIP_KEYWORDS)[number];

/**
 * Pre-compiled word-boundary regex for each keyword, built once at module
 * load.
 */
const KEYWORD_REGEX_MAP: ReadonlyMap<GithubTipKeyword, RegExp> = new Map(
	GITHUB_TIP_KEYWORDS.map((keyword) => {
		const escaped = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
		return [keyword, new RegExp(`\\b${escaped}\\b`, 'i')];
	}),
);

/**
 * Detect whether the current query contains any of the GitHub tip keywords.
 * Returns the FIRST matched keyword (lower-cased) so the dismissal key matches
 * exactly what the user typed. Uses word-boundary matching so "spree" does
 * not match "pr".
 */
export const detectGithubTipKeyword = (query: string): GithubTipKeyword | null => {
	const normalised = query.trim().toLowerCase();
	if (!normalised) {
		return null;
	}
	for (const keyword of GITHUB_TIP_KEYWORDS) {
		const re = KEYWORD_REGEX_MAP.get(keyword);
		if (re && re.test(normalised)) {
			return keyword;
		}
	}
	return null;
};
